import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {
    DialogActionsEnum,
    DialogButtonTypesEnum,
    DialogComponent,
    DialogConfigInterface
} from 'appShared/components';
import {FormInterface, PermissionRuleInterface} from 'appShared/interfaces';
import {Subscription} from 'rxjs';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {
    ApiError,
    ApiResponse,
    HttpStatusCodesEnum,
    OrderRouteService,
    RouteSetGetOutputModel, RouteSetSearchOutputModel, ServerResponseData
} from '@spa/api';
import {Router} from '@angular/router';
import {AlertsService} from 'appShared/services';
import {
    PermissionRuleCheckStrategiesEnum,
    PermissionsEnum
} from 'appShared/enums';
import {FormComponent} from 'appShared/widgets';
import {PanelOrderRoutesMessages} from 'appAssets/messages';
import {
    OrderRoutesAssociationEnum,
    OrderRoutesFormErrorStatesEnum
} from 'appEntries/panel/enums';
import {RouteSetAssociationForm} from 'appEntries/panel/forms';
import {EventsService} from 'appEntries/panel/services';

@Component({
    selector: 'app-associate-route-set',
    templateUrl: './associate-route-set.component.html',
    styleUrls: ['./associate-route-set.component.scss']
})
export class AssociateRouteSetComponent extends DialogComponent<AssociateRouteSetComponent> implements FormInterface, OnDestroy, OnInit {

    protected subscriptions: Subscription[];
    public permissionRule: PermissionRuleInterface;
    public form: RouteSetAssociationForm;
    public orderRouteAssociation: OrderRoutesAssociationEnum;
    public objectId: number;
    public existingRouteSets: number[];
    public availableRouteSets: RouteSetSearchOutputModel;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            config: DialogConfigInterface,
            orderRouteAssociation: OrderRoutesAssociationEnum,
            objectId: number,
            availableRouteSets: RouteSetSearchOutputModel,
            existingRouteSets: number[],
            brokerId?: number,
            currencyCode?: string
        },
        protected router: Router,
        protected alertsService: AlertsService,
        protected orderRouteService: OrderRouteService,
        protected eventsService: EventsService,
        public dialogRef: MatDialogRef<AssociateRouteSetComponent>,
    ) {
        super(dialogRef);
        this.subscriptions = [];
        this.permissionRule = {
            permissions: [PermissionsEnum.CrmTradingAccountsUpdateStatus],
            strategy: PermissionRuleCheckStrategiesEnum.Intersect
        };
        this.config = data.config;
        this.objectId = data.objectId;
        this.orderRouteAssociation = data.orderRouteAssociation;
        this.existingRouteSets = data.existingRouteSets;
        this.availableRouteSets = data.availableRouteSets;
    }

    private setConfirmButtonDisabled(disabled: boolean): void {
        this.config.buttons
            .filter(button => button.type === DialogButtonTypesEnum.Confirm)
            .forEach(button => button.disabled = disabled);
    }

    handleErrorResponse(response: ApiResponse<ApiError[] | RouteSetGetOutputModel>, error: ApiError): string {
        if (Array.isArray(response.messages) && response.messages[0]) {
            return response.messages[0];
        }

        return this.getErrorMessageByApiError(error);
    }

    getRouteSetDetails($event: number) {
        const r = this.availableRouteSets.content.find(value => value.getId() === $event)?.getRoutes().map(value => value.getDisplayName());

        return r.join(', ');
    }

    onFormSubmit(action?: number): void {
        if (action !== DialogActionsEnum.Confirm) {
            this.dialogRef.close(false);

            return;
        }

        if (this.validateForm() && this.form.hasChanges()) {
            const request = this.form.createRequest();
            if (!request) {
                return;
            }

            const requestSubscription = request
                .subscribe((response: ServerResponseData<null>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        (response.data as ApiError[]).map((error: ApiError) => this.handleErrorResponse(response, error))
                            .filter((errorMessage: string) => {
                                return errorMessage.trim();
                            }).forEach((errorMessage: string) => {
                            this.alertsService.addAlert(errorMessage);
                        });

                        return;
                    }

                    this.dialogRef.close(true);
                });
            this.subscriptions.push(requestSubscription);
        }
    }

    getFormMessages(controlLabel?: string): { [p: string]: string } {
        return FormComponent.getFormMessages(controlLabel);
    }

    validateForm(): boolean {
        return this.form.validateForm(this.getFormMessages);
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((subscription: Subscription) => subscription && subscription.unsubscribe());
    }

    ngOnInit(): void {
        this.initForm();
    }

    initForm(): void {
        this.form = new RouteSetAssociationForm(
            this.orderRouteService,
            this.orderRouteAssociation,
            this.objectId,
            this.availableRouteSets,
            this.data.brokerId,
            this.data.currencyCode
        );
        this.form.initForm(this.existingRouteSets);
        this.setConfirmButtonDisabled(!this.form.hasChanges());
        this.subscriptions.push(this.form.formGroup.valueChanges.subscribe(() => {
            this.setConfirmButtonDisabled(!this.form.hasChanges());
        }));
    }

    getSuccessFormMessage(): string {
        return PanelOrderRoutesMessages.RouteSetCreationSucceed.message;
    }

    getErrorMessageByApiError(error: ApiError): string {
        if (error.status === OrderRoutesFormErrorStatesEnum.CreateRouteSetFailed) {
            return PanelOrderRoutesMessages.RouteSetCreationFailed.message;
        }

        return PanelOrderRoutesMessages.UnknownError.message;
    }
}
